import { useState, useMemo, type ReactNode } from "react";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";
import { Link, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  useListInvoices,
  useUpdateInvoice,
  useDuplicateInvoice,
  useDeleteInvoice,
  useSendInvoiceEmail,
  useSendInvoiceReminder,
  useListCustomers,
  getListInvoicesQueryKey,
  getListEstimatesQueryKey,
  useGetQBOBulkSyncStatus,
  useGetXeroBulkSyncStatus,
} from "@workspace/api-client-react";
import { getGetInvoiceQueryOptions, useGetSettings, getGetSettingsQueryKey, useProjectDocumentLinks } from "@workspace/api-client-react";
import { QuickBooksSyncBadge } from "@/components/quickbooks-sync-badge";
import { XeroSyncBadge } from "@/components/xero-sync-badge";
import { QuickProjectAssign } from "@/components/quick-project-assign";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { useQueryClient } from "@tanstack/react-query";
import {
  Search, Plus, Filter, Receipt, CheckCircle, Clock, AlertTriangle, XCircle,
  Copy, Trash2, X, Tag, FileText, ChevronUp, ChevronDown, ChevronsUpDown, RefreshCw,
  CalendarClock, Loader2, Mail, Lock, BellRing, LayoutTemplate, MoreHorizontal,
  PieChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { ProGate } from "@/components/pro-gate";
import { exportInvoicePDF, previewInvoicePDF, invoicePDFBase64, invoicePDFFilename } from "@/lib/pdf";
import { useQuickBooksPush } from "@/hooks/use-quickbooks-push";
import { QuickBooksPushMenuItem } from "@/components/quickbooks-push-menu-item";
import { useXeroPush } from "@/hooks/use-xero-push";
import { XeroPushMenuItem } from "@/components/xero-push-menu-item";
import { PDFPreviewDialog } from "@/components/pdf-preview-dialog";

type InvoiceStatus = "draft" | "sent" | "paid" | "overdue" | "void";
type StatusFilterValue = InvoiceStatus | "all";
type SortKey = "invoiceNumber" | "customerName" | "total" | "invoiceDate" | "dueDate" | "status";
type SortDir = "asc" | "desc";
type InvoiceTab = "one-time" | "recurring";

const STATUS_OPTIONS: { value: InvoiceStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "void", label: "Void" },
];

const VALID_STATUSES = new Set<string>(["draft", "sent", "paid", "overdue", "void"]);
function isInvoiceStatus(v: string): v is InvoiceStatus { return VALID_STATUSES.has(v); }

const STATUS_RANK: Record<string, number> = {
  overdue: 0, sent: 1, draft: 2, paid: 3, void: 4,
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "paid": return "bg-green-100 text-green-800 border-green-300";
    case "sent": return "bg-blue-100 text-blue-800 border-blue-300";
    case "partial": return "bg-amber-100 text-amber-800 border-amber-300";
    case "overdue": return "bg-red-100 text-red-800 border-red-300";
    case "void": return "bg-gray-200 text-gray-600 border-gray-300 line-through";
    default: return "bg-slate-100 text-slate-700 border-slate-300";
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "paid": return <CheckCircle className="w-3.5 h-3.5" />;
    case "sent": return <Clock className="w-3.5 h-3.5" />;
    case "partial": return <PieChart className="w-3.5 h-3.5" />;
    case "overdue": return <AlertTriangle className="w-3.5 h-3.5" />;
    case "void": return <XCircle className="w-3.5 h-3.5" />;
    default: return <FileText className="w-3.5 h-3.5" />;
  }
};

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-40" />;
  return sortDir === "asc"
    ? <ChevronUp className="w-3 h-3 ml-1" />
    : <ChevronDown className="w-3 h-3 ml-1" />;
}

function RecurringGate({ active, children }: { active: boolean; children: ReactNode }) {
  if (!active) return <>{children}</>;
  return <ProGate feature="RECURRING_INVOICES" mode="overlay">{children}</ProGate>;
}

export default function InvoiceList() {
  usePageTitle("Invoices");
  const [activeTab, setActiveTab] = useState<InvoiceTab>("one-time");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>("all");
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<number | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkPushingQbo, setIsBulkPushingQbo] = useState(false);
  const [bulkPushProgress, setBulkPushProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkPushController, setBulkPushController] = useState<AbortController | null>(null);
  const [isBulkPushingXero, setIsBulkPushingXero] = useState(false);
  const [bulkPushProgressXero, setBulkPushProgressXero] = useState<{ current: number; total: number } | null>(null);
  const [bulkPushControllerXero, setBulkPushControllerXero] = useState<AbortController | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>("invoiceDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [sendEmailInvoiceId, setSendEmailInvoiceId] = useState<number | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [fromTemplateOpen, setFromTemplateOpen] = useState(false);
  const [fromTemplateList, setFromTemplateList] = useState<Array<{ id: number; name: string; description: string | null; lineItemCount: number }>>([]);
  const [fromTemplateLoading, setFromTemplateLoading] = useState(false);
  const [fromTemplateSelected, setFromTemplateSelected] = useState<number | null>(null);
  const [ftCustomerId, setFtCustomerId] = useState<number | null>(null);
  const [ftCustomerName, setFtCustomerName] = useState("");
  const [ftCustomerSearch, setFtCustomerSearch] = useState("");
  const [ftCustomerPickerOpen, setFtCustomerPickerOpen] = useState(false);
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const isDesktop = !isMobile;
  const { user } = useAuth();
  const { hasProAccess: isEmailPro } = getTrialInfo(user);


  const listStatus = statusFilter !== "all" ? statusFilter : undefined;
  const debouncedSearch = useDebouncedValue(search);

  const { data: invoices, isLoading, isError, refetch } = useListInvoices({
    search: debouncedSearch || undefined,
    status: listStatus,
  });

  const { data: projectLinks } = useProjectDocumentLinks();

  const updateInvoice = useUpdateInvoice();
  const duplicateInvoice = useDuplicateInvoice();
  const deleteInvoice = useDeleteInvoice();
  const qboPush = useQuickBooksPush("invoice");
  const xeroPush = useXeroPush("invoice");
  const invoiceIdsForQbo = useMemo(
    () => (invoices ?? []).map(inv => inv.id),
    [invoices],
  );
  const { data: qboBulk } = useGetQBOBulkSyncStatus("invoice", invoiceIdsForQbo, {
    enabled: qboPush.connected && invoiceIdsForQbo.length > 0,
  });
  const qboStatuses = qboBulk?.statuses;
  const { data: xeroBulk } = useGetXeroBulkSyncStatus("invoice", invoiceIdsForQbo, {
    enabled: xeroPush.connected && invoiceIdsForQbo.length > 0,
  });
  const xeroStatuses = xeroBulk?.statuses;
  const sendEmailMutation = useSendInvoiceEmail();
  const sendReminderMutation = useSendInvoiceReminder();
  const { data: customers } = useListCustomers();

  const { data: companySettings } = useGetSettings({
    query: { queryKey: getGetSettingsQueryKey() }
  });

  const { formatCurrency, formatDate } = useSettingsFormat();

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  async function openFromTemplate() {
    setFromTemplateSelected(null);
    setFtCustomerId(null);
    setFtCustomerName("");
    setFtCustomerSearch("");
    setFromTemplateLoading(true);
    setFromTemplateOpen(true);
    try {
      const res = await fetch(`${BASE}/api/templates?type=invoice`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      setFromTemplateList(await res.json());
    } catch {
      toast({ title: "Failed to load templates", variant: "destructive" });
      setFromTemplateOpen(false);
    } finally {
      setFromTemplateLoading(false);
    }
  }

  async function createFromTemplate() {
    if (!fromTemplateSelected || !ftCustomerId) return;
    setIsCreatingFromTemplate(true);
    try {
      const res = await fetch(`${BASE}/api/templates/${fromTemplateSelected}/create-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ customerId: ftCustomerId }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setFromTemplateOpen(false);
      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      setLocation(`/invoices/${data.id}`);
    } catch {
      toast({ title: "Failed to create invoice", variant: "destructive" });
    } finally {
      setIsCreatingFromTemplate(false);
    }
  }

  const filteredCustomers = (customers || []).filter(c =>
    c.name.toLowerCase().includes(ftCustomerSearch.toLowerCase())
  );

  const handleTabChange = (tab: InvoiceTab) => {
    setActiveTab(tab);
    setSelectedIds(new Set());
  };

  const sortedInvoices = useMemo(() => {
    if (!invoices) return [];
    const tabFiltered = invoices.filter(inv =>
      activeTab === "recurring" ? inv.isRecurring === true : inv.isRecurring !== true
    );
    return [...tabFiltered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "invoiceNumber":
          cmp = (a.invoiceNumber ?? "").localeCompare(b.invoiceNumber ?? "");
          break;
        case "customerName":
          cmp = (a.customerName ?? "").localeCompare(b.customerName ?? "");
          break;
        case "total":
          cmp = a.total - b.total;
          break;
        case "invoiceDate":
          cmp = (a.invoiceDate ?? "0000").localeCompare(b.invoiceDate ?? "0000");
          break;
        case "dueDate":
          cmp = (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999");
          break;
        case "status":
          cmp = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [invoices, sortKey, sortDir, activeTab]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const oneTimeChildrenByParent = useMemo(() => {
    if (activeTab !== "one-time") return new Map<number, typeof sortedInvoices>();
    const map = new Map<number, typeof sortedInvoices>();
    for (const inv of sortedInvoices) {
      const parentId = inv.depositParentId ?? inv.milestoneParentId ?? null;
      if (parentId !== null) {
        const existing = map.get(parentId) ?? [];
        existing.push(inv);
        map.set(parentId, existing);
      }
    }
    return map;
  }, [sortedInvoices, activeTab]);

  const topLevelInvoices = useMemo(() => {
    if (activeTab !== "one-time") return sortedInvoices;
    const allIds = new Set(sortedInvoices.map(inv => inv.id));
    return sortedInvoices.filter(inv => {
      const parentId = inv.depositParentId ?? inv.milestoneParentId ?? null;
      if (parentId === null) return true;
      return !allIds.has(parentId);
    });
  }, [sortedInvoices, activeTab]);

  const allIds = topLevelInvoices.map(inv => inv.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;
  const indeterminate = someSelected && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleDuplicate = (id: number) => {
    duplicateInvoice.mutate({ id }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        toast({ title: "Invoice duplicated" });
        setLocation(`/invoices/${result.id}`);
      },
      onError: () => toast({ title: "Failed to duplicate invoice", variant: "destructive" }),
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteInvoiceId === null) return;
    deleteInvoice.mutate({ id: deleteInvoiceId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        toast({ title: "Invoice deleted" });
        setDeleteInvoiceId(null);
      },
      onError: () => {
        toast({ title: "Failed to delete invoice", variant: "destructive" });
        setDeleteInvoiceId(null);
      },
    });
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map(id => deleteInvoice.mutateAsync({ id })));
      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      toast({ title: `Deleted ${ids.length} invoice${ids.length !== 1 ? "s" : ""}` });
      clearSelection();
    } catch {
      toast({ title: "Some deletes failed", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteOpen(false);
    }
  };

  const handleBulkPushQbo = async () => {
    if (!qboPush.available) {
      qboPush.pushMany([]);
      return;
    }
    const ids = Array.from(selectedIds);
    const controller = new AbortController();
    setBulkPushController(controller);
    setBulkPushProgress({ current: 0, total: ids.length });
    setIsBulkPushingQbo(true);
    try {
      const summary = await qboPush.pushMany(ids, {
        signal: controller.signal,
        onProgress: (p) => setBulkPushProgress({ current: p.current, total: p.total }),
      });
      if (summary && summary.failed === 0 && !summary.cancelled) {
        clearSelection();
      } else if (summary) {
        const remaining = ids.slice(summary.processed);
        const failedIds = new Set<number>([
          ...summary.failures.map(f => f.id),
          ...remaining,
        ]);
        setSelectedIds(failedIds);
      }
    } finally {
      setIsBulkPushingQbo(false);
      setBulkPushProgress(null);
      setBulkPushController(null);
    }
  };

  const cancelBulkPushQbo = () => {
    bulkPushController?.abort();
  };

  const handleBulkPushXero = async () => {
    if (!xeroPush.available) {
      xeroPush.pushMany([]);
      return;
    }
    const ids = Array.from(selectedIds);
    const controller = new AbortController();
    setBulkPushControllerXero(controller);
    setBulkPushProgressXero({ current: 0, total: ids.length });
    setIsBulkPushingXero(true);
    try {
      const summary = await xeroPush.pushMany(ids, {
        signal: controller.signal,
        onProgress: (p) => setBulkPushProgressXero({ current: p.current, total: p.total }),
      });
      if (summary && summary.failed === 0 && !summary.cancelled) {
        clearSelection();
      } else if (summary) {
        const remaining = ids.slice(summary.processed);
        const failedIds = new Set<number>([
          ...summary.failures.map(f => f.id),
          ...remaining,
        ]);
        setSelectedIds(failedIds);
      }
    } finally {
      setIsBulkPushingXero(false);
      setBulkPushProgressXero(null);
      setBulkPushControllerXero(null);
    }
  };

  const cancelBulkPushXero = () => {
    bulkPushControllerXero?.abort();
  };

  const handleBulkStatusChange = (value: string) => {
    if (!isInvoiceStatus(value)) return;
    const ids = Array.from(selectedIds);
    Promise.all(ids.map(id => updateInvoice.mutateAsync({ id, data: { status: value } })))
      .then(() => {
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        toast({ title: `Updated ${ids.length} invoice${ids.length !== 1 ? "s" : ""} to ${value}` });
      })
      .catch(() => {
        toast({ title: "Some updates failed", variant: "destructive" });
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      });
  };

  const handleExportPDF = async (id: number) => {
    try {
      const detail = await queryClient.fetchQuery(getGetInvoiceQueryOptions(id));
      const invoiceForPdf = { ...detail, createdAt: String(detail.createdAt) };
      await exportInvoicePDF(invoiceForPdf, companySettings ?? {});
    } catch {
      toast({ title: "Export failed", description: "Could not load invoice details.", variant: "destructive" });
    }
  };

  const handlePreviewPDF = async (id: number) => {
    try {
      const detail = await queryClient.fetchQuery(getGetInvoiceQueryOptions(id));
      const invoiceForPdf = { ...detail, createdAt: String(detail.createdAt) };
      const url = await previewInvoicePDF(invoiceForPdf, companySettings ?? {});
      setPreviewBlobUrl(url);
      setPreviewTitle("Invoice PDF");
      setPreviewOpen(true);
    } catch {
      toast({ title: "Preview failed", description: "Could not load invoice details.", variant: "destructive" });
    }
  };

  const handleOpenSendEmail = (invoice: { id: number; invoiceNumber?: string | null; customerName?: string | null }) => {
    setSendEmailInvoiceId(invoice.id);
    const applyVars = (template: string, vars: Record<string, string>) =>
      template.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
    const vars = {
      customerName: invoice.customerName || "there",
      quoteNumber: "",
      invoiceNumber: invoice.invoiceNumber || "",
      companyName: companySettings?.companyName || "us",
    };
    const defaultSubject = `Invoice ${invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : ""} from ${companySettings?.companyName || "us"}`;
    const defaultBody = `Hi ${invoice.customerName || "there"},\n\nPlease find your invoice attached. Payment is due as per the invoice terms.\n\nThank you for your business!`;
    setEmailSubject(companySettings?.defaultInvoiceEmailSubject ? applyVars(companySettings.defaultInvoiceEmailSubject, vars) : defaultSubject);
    setEmailMessage(companySettings?.defaultInvoiceEmailBody ? applyVars(companySettings.defaultInvoiceEmailBody, vars) : defaultBody);
  };

  const handleSendReminder = async (id: number) => {
    sendReminderMutation.mutate({ id }, {
      onSuccess: (result) => {
        toast({ title: "Overdue reminder sent", description: `Sent to ${result.to}` });
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        const message = e?.response?.data?.error || e?.message || "Failed to send reminder";
        toast({ title: message, variant: "destructive" });
      },
    });
  };

  const onStatusChange = (id: number, value: string, dueDate?: string | null) => {
    if (!isInvoiceStatus(value)) return;
    if (value === "overdue") {
      const today = new Date(new Date().toDateString());
      if (!dueDate || new Date(dueDate) >= today) {
        toast({ title: "Cannot mark as overdue: due date has not passed yet", variant: "destructive" });
        return;
      }
    }
    updateInvoice.mutate({ id, data: { status: value } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        toast({ title: `Status updated to ${value}` });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        const message = e?.response?.data?.error || e?.message || "Failed to update status";
        toast({ title: message, variant: "destructive" });
      },
    });
  };

  const SortHeader = ({ col, label, align = "left" }: { col: SortKey; label: string; align?: "left" | "right" | "center" }) => (
    <button
      className={`flex items-center gap-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors ${align === "right" ? "ml-auto" : align === "center" ? "mx-auto" : ""}`}
      onClick={() => handleSort(col)}
      type="button"
    >
      {label}
      <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
    </button>
  );

  const isRecurringTab = activeTab === "recurring";

  const desktopGridCols = isRecurringTab
    ? "36px 90px 1fr 130px 130px 160px 150px 80px"
    : "36px 90px 1fr 130px 130px 160px 150px 80px";

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className={`flex ${isDesktop ? "flex-row items-center" : "flex-col"} justify-between gap-4`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Receipt className="w-8 h-8 text-primary" />
            Invoices
          </h1>
          <p className="text-muted-foreground mt-1">Create and manage your invoices.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openFromTemplate}>
            <LayoutTemplate className="w-4 h-4 mr-2" />
            From Template
          </Button>
          <Link href="/invoices/new">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Invoice
            </Button>
          </Link>
        </div>
      </div>

      {/* Tabs — visually distinct with filled active state */}
      <div className="flex gap-1 p-1 bg-border rounded-lg w-full">
        <button
          type="button"
          onClick={() => handleTabChange("one-time")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-base font-medium transition-all ${
            activeTab === "one-time"
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Receipt className="w-4 h-4" />
          One-time
        </button>
        <ProGate feature="RECURRING_INVOICES" mode="overlay">
          <button
            type="button"
            onClick={() => handleTabChange("recurring")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-base font-medium transition-all ${
              activeTab === "recurring"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <RefreshCw className="w-4 h-4" />
            Recurring
          </button>
        </ProGate>
      </div>

      <div className={`flex ${isDesktop ? "flex-row" : "flex-col"} gap-4`}>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by #, customer, or title…"
            className="pl-10 bg-card"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ width: isDesktop ? "12rem" : "100%" }}>
          <Select value={statusFilter} onValueChange={(v) => (isInvoiceStatus(v) || v === "all") && setStatusFilter(v as StatusFilterValue)}>
            <SelectTrigger className="bg-card">
              <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {someSelected && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium text-primary mr-1">
            {selectedIds.size} selected
          </span>
          <Select onValueChange={handleBulkStatusChange}>
            <SelectTrigger className="h-8 w-auto gap-1.5 border-primary/30 text-primary bg-transparent text-xs font-medium px-2.5" style={{ minWidth: "130px" }}>
              <Tag className="w-3.5 h-3.5" />
              <SelectValue placeholder="Set status…" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.filter(s => s.value !== "overdue").map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {qboPush.connected && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                onClick={handleBulkPushQbo}
                disabled={isBulkPushingQbo || qboPush.needsReconnect}
                data-testid="button-bulk-push-qbo-invoices"
                title={qboPush.needsReconnect ? "Reconnect QuickBooks in Integrations to push." : undefined}
              >
                {isBulkPushingQbo ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center text-[9px] font-bold rounded-sm bg-[#2CA01C] text-white">Q</span>
                )}
                {isBulkPushingQbo
                  ? bulkPushProgress
                    ? `Pushing ${Math.min(bulkPushProgress.current, bulkPushProgress.total)} of ${bulkPushProgress.total}…`
                    : "Pushing…"
                  : "Push to QuickBooks"}
              </Button>
              {isBulkPushingQbo && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={cancelBulkPushQbo}
                  data-testid="button-cancel-bulk-push-qbo-invoices"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </Button>
              )}
            </>
          )}
          {xeroPush.connected && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                onClick={handleBulkPushXero}
                disabled={isBulkPushingXero || xeroPush.needsReconnect}
                data-testid="button-bulk-push-xero-invoices"
                title={xeroPush.needsReconnect ? "Reconnect Xero in Integrations to push." : undefined}
              >
                {isBulkPushingXero ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center text-[9px] font-bold rounded-sm bg-[#13B5EA] text-white">X</span>
                )}
                {isBulkPushingXero
                  ? bulkPushProgressXero
                    ? `Pushing ${Math.min(bulkPushProgressXero.current, bulkPushProgressXero.total)} of ${bulkPushProgressXero.total}…`
                    : "Pushing…"
                  : "Push to Xero"}
              </Button>
              {isBulkPushingXero && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={cancelBulkPushXero}
                  data-testid="button-cancel-bulk-push-xero-invoices"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </Button>
              )}
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 ml-auto text-muted-foreground hover:text-foreground"
            onClick={clearSelection}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      <RecurringGate active={activeTab === "recurring"}>
        {isError ? (
        <div className="text-center py-16 bg-card rounded-lg border border-dashed">
          <Receipt className="mx-auto h-12 w-12 text-destructive/50 mb-4" />
          <h3 className="text-lg font-medium">Failed to load invoices</h3>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            Something went wrong while fetching your invoices.
          </p>
          <Button className="mt-4" variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-1/4" />
                </div>
                <div className="space-y-2 flex flex-col items-end">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sortedInvoices.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-lg border border-dashed">
          <Receipt className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">No invoices found</h3>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            {search || statusFilter !== "all"
              ? "No invoices matched your filters."
              : activeTab === "recurring"
                ? "You haven't set up any recurring invoices yet."
                : "You haven't created any invoices yet."}
          </p>
          {!search && statusFilter === "all" && (
            <Link href="/invoices/new">
              <Button className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Invoice
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border/50 shadow-md overflow-hidden">
          {isDesktop && (
            <div
              style={{ display: "grid", gridTemplateColumns: desktopGridCols }}
              className="gap-3 px-4 py-3 border-b bg-muted/50"
            >
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={indeterminate ? "indeterminate" : allSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all invoices"
                />
              </div>
              <SortHeader col="invoiceNumber" label="#" />
              <SortHeader col="customerName" label="Customer / Title" />
              <div className="flex justify-end"><SortHeader col="total" label="Amount" align="right" /></div>
              <div className="flex justify-center"><SortHeader col="dueDate" label="Due Date" align="center" /></div>
              <div className="flex justify-center"><SortHeader col="status" label="Status" align="center" /></div>
              {isRecurringTab ? (
                <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap overflow-hidden">
                  <CalendarClock className="w-3 h-3 shrink-0" />
                  <span className="truncate">Next Gen.</span>
                  <InfoTip content={FORMULAS.invoiceNextGenCol} />
                </div>
              ) : (
                <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap overflow-hidden">
                  <BellRing className="w-3 h-3 shrink-0" />
                  <span className="truncate">Reminder</span>
                  <InfoTip content={FORMULAS.invoiceReminderCol} />
                </div>
              )}
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Actions</div>
            </div>
          )}

          <div>
            {topLevelInvoices.map((invoice) => {
              const childRows = oneTimeChildrenByParent.get(invoice.id) ?? [];
              return (
              <div key={invoice.id} className="border-b border-border last:border-b-0">
              <div
                style={isDesktop ? {
                  display: "grid",
                  gridTemplateColumns: desktopGridCols,
                  alignItems: "center",
                } : { display: "block" }}
                className={`gap-3 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer ${selectedIds.has(invoice.id) ? "bg-primary/5" : ""}`}
                onClick={() => setLocation(`/invoices/${invoice.id}`)}
              >
                {isDesktop && (
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(invoice.id)}
                      onCheckedChange={() => toggleSelect(invoice.id)}
                      aria-label={`Select invoice ${invoice.invoiceNumber || invoice.id}`}
                    />
                  </div>
                )}

                {isDesktop && (
                  <div className="text-xs font-mono text-muted-foreground">
                    {invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : "—"}
                  </div>
                )}

                <div className="flex flex-col min-w-0">
                  <div className="font-semibold text-foreground truncate text-sm flex items-center gap-1.5">
                    {invoice.customerName || invoice.title}
                    {invoice.isRecurring && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded font-medium shrink-0">
                        <RefreshCw className="w-2.5 h-2.5" /> Recurring
                      </span>
                    )}
                    {qboPush.connected && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <QuickBooksSyncBadge kind="invoice" status={qboStatuses?.[String(invoice.id)]} />
                      </span>
                    )}
                    {xeroPush.connected && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <XeroSyncBadge kind="invoice" status={xeroStatuses?.[String(invoice.id)]} />
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {!isDesktop && invoice.invoiceNumber && (
                      <span className="font-mono">#{invoice.invoiceNumber} · </span>
                    )}
                    {isDesktop && <span>{formatDate(invoice.invoiceDate ?? invoice.createdAt)}</span>}
                    {invoice.title !== invoice.customerName ? <span>{invoice.title}</span> : null}
                    {!isDesktop && invoice.invoiceDate && <span>· {formatDate(invoice.invoiceDate)}</span>}
                    {!isDesktop && invoice.dueDate && <span>· Due {formatDate(invoice.dueDate)}</span>}
                    <QuickProjectAssign kind="invoice" id={invoice.id} link={projectLinks?.invoices[String(invoice.id)]} />
                  </div>
                </div>

                {isDesktop && (
                  <div className="text-right">
                    <div className="font-semibold text-sm">{formatCurrency(invoice.total)}</div>
                    {(invoice.paidAmount ?? 0) > 0 && (invoice.paidAmount ?? 0) < invoice.total && (
                      <div className="text-xs text-muted-foreground">
                        {formatCurrency(Math.max(0, invoice.total - (invoice.paidAmount ?? 0)))} owing
                      </div>
                    )}
                  </div>
                )}

                {isDesktop && (
                  <div className="text-sm text-muted-foreground text-center">
                    {invoice.dueDate ? formatDate(invoice.dueDate) : "—"}
                  </div>
                )}

                {isDesktop && (
                  <div style={{ display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                    {invoice.isContainer ? (
                      <span
                        className={`h-7 text-xs font-medium px-2.5 border rounded-full gap-1.5 flex items-center ${getStatusColor(invoice.status)}`}
                        style={{ minWidth: "110px" }}
                        title="Status is derived from its deposit/balance/milestone invoices"
                      >
                        {getStatusIcon(invoice.status)}
                        <span className="flex-1 text-left capitalize">{invoice.status}</span>
                      </span>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`h-7 text-xs font-medium px-2.5 border rounded-full gap-1.5 flex items-center ${getStatusColor(invoice.status)}`}
                            style={{ minWidth: "110px" }}
                          >
                            {getStatusIcon(invoice.status)}
                            <span className="flex-1 text-left capitalize">{invoice.status}</span>
                            <ChevronDown className="w-3 h-3 opacity-60" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center">
                          {STATUS_OPTIONS.map((s) => {
                            const isOverdueDisabled = s.value === "overdue" && (!invoice.dueDate || new Date(invoice.dueDate) >= new Date(new Date().toDateString()));
                            return (
                              <DropdownMenuItem
                                key={s.value}
                                disabled={isOverdueDisabled}
                                onSelect={() => !isOverdueDisabled && onStatusChange(invoice.id, s.value, invoice.dueDate)}
                                className="flex items-center gap-2 text-xs"
                              >
                                {getStatusIcon(s.value)}
                                {s.label}
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}

                {isDesktop && isRecurringTab && (
                  <div className="text-sm text-muted-foreground flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {invoice.recurringNextDate ? (
                      <>
                        <CalendarClock className="w-3.5 h-3.5 shrink-0 text-violet-500" />
                        <span>{formatDate(invoice.recurringNextDate)}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </div>
                )}

                {isDesktop && !isRecurringTab && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    {invoice.lastReminderSentAt ? (
                      <>
                        <BellRing className="w-3 h-3 shrink-0 text-red-400" />
                        <span>{formatDate(String(invoice.lastReminderSentAt).slice(0, 10))}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </div>
                )}

                {!isDesktop && (
                  <div className="flex items-center justify-between mt-2">
                    <div>
                      <span className="font-semibold text-sm">{formatCurrency(invoice.total)}</span>
                      {(invoice.paidAmount ?? 0) > 0 && (invoice.paidAmount ?? 0) < invoice.total && (
                        <span className="text-xs text-muted-foreground ml-1.5">
                          ({formatCurrency(Math.max(0, invoice.total - (invoice.paidAmount ?? 0)))} owing)
                        </span>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </div>
                )}

                {!isDesktop && isRecurringTab && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                    <CalendarClock className="w-3.5 h-3.5 text-violet-500" />
                    {invoice.recurringNextDate ? (
                      <span>Next: {formatDate(invoice.recurringNextDate)}</span>
                    ) : (
                      <span className="text-muted-foreground/50">Next: —</span>
                    )}
                  </div>
                )}

                {!isDesktop && !isRecurringTab && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                    <BellRing className="w-3 h-3 text-red-400 shrink-0" />
                    {invoice.lastReminderSentAt ? (
                      <span>Reminded: {formatDate(String(invoice.lastReminderSentAt).slice(0, 10))}</span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" aria-label="Invoice actions" className="h-7 px-2 text-muted-foreground hover:text-foreground">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onClick={() => setLocation(`/invoices/${invoice.id}`)}>
                        Open
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleExportPDF(invoice.id)}>
                        <FileText className="w-3.5 h-3.5 mr-2 text-blue-500" />
                        Export PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handlePreviewPDF(invoice.id)}>
                        <FileText className="w-3.5 h-3.5 mr-2 text-blue-500" />
                        Preview PDF
                      </DropdownMenuItem>
                      {!invoice.isContainer && (
                        <>
                          <DropdownMenuSeparator />
                          {isEmailPro ? (
                            <DropdownMenuItem onClick={() => handleOpenSendEmail(invoice)}>
                              <Mail className="w-3.5 h-3.5 mr-2" />
                              Send Email
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem asChild>
                              <Link href="/upgrade" className="flex items-center gap-2 text-muted-foreground">
                                <Lock className="w-3.5 h-3.5 text-amber-500" />
                                <Mail className="w-3.5 h-3.5 mr-2" />
                                Send Email
                              </Link>
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      {invoice.status === "overdue" && invoice.overdueReminderEnabled && (
                        isEmailPro ? (
                          <DropdownMenuItem onClick={() => handleSendReminder(invoice.id)}>
                            <BellRing className="w-3.5 h-3.5 mr-2 text-red-500" />
                            Send Reminder
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem asChild>
                            <Link href="/upgrade" className="flex items-center gap-2 text-muted-foreground">
                              <Lock className="w-3.5 h-3.5 text-amber-500" />
                              <BellRing className="w-3.5 h-3.5 mr-2" />
                              Send Reminder
                            </Link>
                          </DropdownMenuItem>
                        )
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDuplicate(invoice.id)}>
                        <Copy className="w-3.5 h-3.5 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      {qboPush.connected && (
                        <>
                          <DropdownMenuSeparator />
                          <QuickBooksPushMenuItem
                            kind="invoice"
                            id={invoice.id}
                            testId={`menu-qbo-push-invoice-${invoice.id}`}
                          />
                        </>
                      )}
                      {xeroPush.connected && (
                        <>
                          <DropdownMenuSeparator />
                          <XeroPushMenuItem
                            kind="invoice"
                            id={invoice.id}
                            testId={`menu-xero-push-invoice-${invoice.id}`}
                          />
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleteInvoiceId(invoice.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {childRows.map(child => (
                <div
                  key={child.id}
                  style={isDesktop ? {
                    display: "grid",
                    gridTemplateColumns: desktopGridCols,
                    alignItems: "center",
                    paddingLeft: "2.5rem",
                    background: "hsl(var(--muted)/40%)",
                  } : { display: "block", paddingLeft: "1.5rem", background: "hsl(var(--muted)/40%)" }}
                  className="gap-3 px-4 py-2 border-t border-dashed border-border/60 hover:bg-muted/60 transition-colors cursor-pointer text-sm"
                  onClick={() => setLocation(`/invoices/${child.id}`)}
                >
                  {isDesktop && <div />}
                  {isDesktop && (
                    <div className="text-xs font-mono text-muted-foreground/70">
                      {child.invoiceNumber ? `#${child.invoiceNumber}` : "—"}
                    </div>
                  )}
                  <div className="flex flex-col min-w-0">
                    <div className="text-sm text-foreground/80 truncate flex items-center gap-1.5">
                      {child.isDepositChild && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded shrink-0">Deposit</span>
                      )}
                      {child.milestoneParentId != null && !child.isDepositChild && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-blue-700 bg-blue-50 border border-blue-300 px-1.5 py-0.5 rounded shrink-0">Milestone</span>
                      )}
                      {child.customerName || child.title}
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {!isDesktop && child.invoiceNumber && <span className="font-mono">#{child.invoiceNumber} · </span>}
                      {child.title !== child.customerName ? <span>{child.title}</span> : null}
                    </div>
                  </div>
                  {isDesktop && (
                    <div className="text-sm text-right text-muted-foreground">{formatCurrency(child.total)}</div>
                  )}
                  {isDesktop && (
                    <div className="text-sm text-muted-foreground text-center">
                      {child.dueDate ? formatDate(child.dueDate) : "—"}
                    </div>
                  )}
                  {isDesktop && (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <span className={`h-6 text-xs font-medium px-2.5 border rounded-full flex items-center gap-1 ${getStatusColor(child.status)}`}>
                        {getStatusIcon(child.status)}
                        <span className="capitalize">{child.status}</span>
                      </span>
                    </div>
                  )}
                  {isDesktop && <div />}
                  {isDesktop && (
                    <div style={{ display: "flex", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={(e) => { e.stopPropagation(); setLocation(`/invoices/${child.id}`); }}>
                        Open
                      </Button>
                    </div>
                  )}
                  {!isDesktop && (
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm">{formatCurrency(child.total)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${getStatusColor(child.status)}`}>{child.status}</span>
                    </div>
                  )}
                </div>
              ))}
              </div>
              );
            })}
          </div>
        </div>
        )}
      </RecurringGate>

      <AlertDialog open={deleteInvoiceId !== null} onOpenChange={(open) => !open && setDeleteInvoiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              This invoice will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => { if (!open) setBulkDeleteOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Invoice{selectedIds.size !== 1 ? "s" : ""}</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedIds.size} invoice{selectedIds.size !== 1 ? "s" : ""}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleBulkDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PDFPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        blobUrl={previewBlobUrl}
        title={previewTitle}
        fileName="invoice.pdf"
      />

      {(() => {
        const sendEmailInvoice = invoices?.find(inv => inv.id === sendEmailInvoiceId) ?? null;
        return (
          <Dialog open={sendEmailInvoiceId !== null} onOpenChange={(open) => { if (!open) { setSendEmailInvoiceId(null); setCcSelf(true); } }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Send Invoice via Email
                </DialogTitle>
                <DialogDescription>
                  {sendEmailInvoice?.customerEmail
                    ? <>Sending to <span className="font-medium text-foreground">{sendEmailInvoice.customerEmail}</span></>
                    : <span className="text-destructive">No email on file for this customer. Please add one to their record first.</span>
                  }
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <label className="text-sm font-medium block mb-1">Subject</label>
                  <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Message</label>
                  <Textarea
                    value={emailMessage}
                    onChange={e => setEmailMessage(e.target.value)}
                    className="min-h-[120px] resize-none text-sm"
                  />
                </div>
                {companySettings?.replyToEmail ? (
                  <div className="flex items-center gap-2">
                    <input
                      id="cc-self-invoice-list"
                      type="checkbox"
                      checked={ccSelf}
                      onChange={e => setCcSelf(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                    />
                    <label htmlFor="cc-self-invoice-list" className="text-sm cursor-pointer select-none">
                      Send a copy to myself <span className="text-muted-foreground">({companySettings.replyToEmail})</span>
                    </label>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    To CC yourself on sent invoices, add a reply-to email in{" "}
                    <a href="/settings" className="underline hover:text-foreground transition-colors">Settings</a>.
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setSendEmailInvoiceId(null); setCcSelf(true); }}>Cancel</Button>
                <Button
                  disabled={!sendEmailInvoice?.customerEmail || sendEmailMutation.isPending || !emailSubject || !emailMessage}
                  onClick={async () => {
                    if (!sendEmailInvoice?.customerEmail || !sendEmailInvoiceId) return;
                    try {
                      const detail = await queryClient.fetchQuery(getGetInvoiceQueryOptions(sendEmailInvoiceId));
                      const invoiceForPdf = { ...detail, createdAt: String(detail.createdAt) };
                      const attachmentBase64 = await invoicePDFBase64(invoiceForPdf, companySettings ?? {});
                      const attachmentFilename = invoicePDFFilename(invoiceForPdf);
                      await sendEmailMutation.mutateAsync({
                        id: sendEmailInvoiceId,
                        body: { subject: emailSubject, message: emailMessage, attachmentBase64, attachmentFilename, ccSelf },
                      });
                      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
                      toast({ title: "Invoice sent", description: `Email sent to ${sendEmailInvoice.customerEmail}` });
                      setSendEmailInvoiceId(null);
                      setCcSelf(true);
                    } catch (err: unknown) {
                      const e = err as { response?: { data?: { error?: string } }; message?: string };
                      const message = e?.response?.data?.error || e?.message || "Failed to send email";
                      toast({ title: message, variant: "destructive" });
                    }
                  }}
                >
                  {sendEmailMutation.isPending ? "Sending…" : "Send Email"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      <Dialog open={fromTemplateOpen} onOpenChange={o => { if (!isCreatingFromTemplate) setFromTemplateOpen(o); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Invoice from Template</DialogTitle>
            <DialogDescription>Pick a template and choose a customer to create a new draft invoice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {fromTemplateLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : fromTemplateList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No invoice templates saved yet. Open an invoice, click ⋮, and choose "Save as Template".
              </div>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {fromTemplateList.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFromTemplateSelected(t.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${fromTemplateSelected === t.id ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/40 hover:bg-muted/40"}`}
                  >
                    <div className="font-medium text-sm">{t.name}</div>
                    {t.description && <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>}
                    <div className="text-xs text-muted-foreground mt-0.5">{t.lineItemCount} line item{t.lineItemCount !== 1 ? "s" : ""}</div>
                  </button>
                ))}
              </div>
            )}

            {fromTemplateSelected !== null && (
              <div>
                <label className="text-sm font-medium block mb-2">Select Customer</label>
                <Popover open={ftCustomerPickerOpen} onOpenChange={setFtCustomerPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between">
                      <span className={!ftCustomerName ? "text-muted-foreground" : ""}>{ftCustomerName || "Choose a customer…"}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command>
                      <CommandInput placeholder="Search customers…" value={ftCustomerSearch} onValueChange={setFtCustomerSearch} />
                      <CommandList>
                        <CommandEmpty>No customers found.</CommandEmpty>
                        <CommandGroup>
                          {filteredCustomers.map(c => (
                            <CommandItem key={c.id} value={c.name} onSelect={() => { setFtCustomerId(c.id); setFtCustomerName(c.name); setFtCustomerPickerOpen(false); setFtCustomerSearch(""); }}>
                              {c.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isCreatingFromTemplate} onClick={() => setFromTemplateOpen(false)}>Cancel</Button>
            <Button disabled={!fromTemplateSelected || !ftCustomerId || isCreatingFromTemplate} onClick={createFromTemplate}>
              {isCreatingFromTemplate ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
